import React, { useState, useContext } from "react";
import { Typography } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";

import { getToken } from "../../../../api/Account";
import { tienePermiso } from "../../../../helpers/permisos";
import { DataContext } from "../../../../context/DataContext";
import { EstadoChip, useAviso } from "../../../../components/base";
import { formatearMonto } from "../../../../helpers/formato";
import MediumModal from "../../../../components/modals/MediumModal";
import {
  deleteBonoAsync,
  getBonosByEmployeeAsync,
  payBonoAsync,
} from "../../../../api/Employees";
import AddBono from "../bonos/AddBono";
import UpdateBono from "../bonos/UpdateBono";
import PrintComprobante from "../bonos/PrintComprobante";
import HistorialTab from "./HistorialTab";
import {
  celdaFecha,
  celdaMonto,
  periodo,
  useConfirmacion,
  useEscritura,
  useListaRemota,
} from "../comunes";

const EstadoBono = ({ pagado }) =>
  pagado ? (
    <EstadoChip estado="ok" label="Pagado" />
  ) : (
    <EstadoChip estado="aviso" label="Pendiente" />
  );

const Bonos = ({ id }) => {
  const [selectedBono, setSelectedBono] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);

  const token = getToken();
  const { access } = useContext(DataContext);
  const aviso = useAviso();
  const escribir = useEscritura();
  const { pedir, dialogo } = useConfirmacion();

  const {
    datos: histoyBonos,
    cargando,
    error,
    reload: localReload,
    setReload: setLocalReload,
  } = useListaRemota(() => getBonosByEmployeeAsync(token, id));

  const deleteBono = async (bonoId) => {
    const ok = await escribir(
      () => deleteBonoAsync(token, bonoId),
      "No se pudo eliminar el bono"
    );
    if (!ok) return;
    setLocalReload(!localReload);
    aviso.exito("Bono eliminado");
  };

  const payBono = async (bonoId) => {
    const ok = await escribir(() => payBonoAsync(token, bonoId), "No se pudo pagar el bono");
    if (!ok) return;
    setLocalReload(!localReload);
    aviso.exito("Bono pagado");
  };

  const descripcionDe = (row) =>
    `bono #${row.id}${row.description ? ` «${row.description}»` : ""} de ${formatearMonto(
      row.monto
    )} (${periodo(row.desde, row.hasta)})`;

  const pedirEliminar = (row) =>
    pedir({
      titulo: "Eliminar bono",
      mensaje: (
        <>
          <Typography component="p" sx={{ mb: 1 }}>
            {`Se eliminará el ${descripcionDe(row)}.`}
          </Typography>
          <Typography component="p" sx={{ color: "text.secondary" }}>
            Esta acción no se puede deshacer.
          </Typography>
        </>
      ),
      textoConfirmar: "Eliminar bono",
      peligro: true,
      onConfirmar: () => deleteBono(row.id),
    });

  const pedirPagar = (row) =>
    pedir({
      titulo: "Pagar bono",
      mensaje: (
        <Typography component="p">
          {`Se registrará el pago del ${descripcionDe(row)}.`}
        </Typography>
      ),
      textoConfirmar: "Pagar bono",
      onConfirmar: () => payBono(row.id),
    });

  // Agregar es el Add... de EmployeesController/NominasController (NOMINA CREAR).
  const puedeAgregar = tienePermiso(access, "NOMINA CREAR");
  const puedeEditar = tienePermiso(access, "NOMINA UPDATE");
  // La pantalla pedía NOMINA DELETE y DeleteBono exige NOMINA UPDATE en el servidor: se piden los dos.
  const puedeEliminar =
    tienePermiso(access, "NOMINA DELETE") && tienePermiso(access, "NOMINA UPDATE");

  const columnas = [
    {
      id: "id",
      etiqueta: "#",
      desde: "md",
      sx: { fontVariantNumeric: "tabular-nums" },
      render: (row) => row.id,
    },
    {
      id: "descripcion",
      etiqueta: "Descripción",
      render: (row) => (
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {row.description}
        </Typography>
      ),
    },
    {
      id: "periodo",
      etiqueta: "Período",
      desde: "sm",
      sx: celdaFecha,
      render: (row) => periodo(row.desde, row.hasta),
    },
    {
      id: "monto",
      etiqueta: "Monto",
      alinear: "right",
      sx: celdaMonto,
      render: (row) => (
        <Typography variant="monto" component="span">
          {formatearMonto(row.monto)}
        </Typography>
      ),
    },
    {
      id: "estado",
      etiqueta: "Estado",
      desde: "sm",
      render: (row) => <EstadoBono pagado={row.isPayed} />,
    },
  ];

  return (
    <div>
      <HistorialTab
        titulo="Bonos"
        descripcion="Bonos del empleado y su pago."
        etiquetaTabla="Historial de bonos"
        agregar={
          puedeAgregar ? { etiqueta: "Agregar bono", onClick: () => setShowModal(true) } : undefined
        }
        cargando={cargando}
        error={error}
        onReintentar={() => setLocalReload(!localReload)}
        etiquetaCarga="Cargando bonos…"
        filas={histoyBonos}
        columnas={columnas}
        detalleMovil={(row) => (
          <>
            <Typography variant="caption" component="span" sx={{ display: "block", mb: 0.5 }}>
              {periodo(row.desde, row.hasta)}
            </Typography>
            <EstadoBono pagado={row.isPayed} />
          </>
        )}
        etiquetaFila={(row) => `bono #${row.id}`}
        vacio={{
          titulo: "Sin bonos",
          descripcion: "Aquí aparecen los bonos del empleado.",
        }}
        acciones={(row) => [
          // Pagar mientras no se pague y se pueda; si no, el comprobante (como siempre).
          !row.isPayed && puedeEditar
            ? {
                id: "pagar",
                etiqueta: "Pagar",
                icono: <PaidOutlinedIcon />,
                onClick: () => pedirPagar(row),
              }
            : {
                id: "imprimir",
                etiqueta: "Imprimir comprobante",
                icono: <PrintOutlinedIcon />,
                onClick: () => {
                  setSelectedBono(row);
                  setShowPrintModal(true);
                },
              },
          {
            id: "editar",
            etiqueta: "Editar",
            icono: <EditOutlinedIcon />,
            visible: puedeEditar,
            onClick: () => {
              setSelectedBono(row);
              setShowEditModal(true);
            },
          },
          {
            id: "eliminar",
            etiqueta: "Eliminar",
            icono: <DeleteOutlineIcon />,
            peligro: true,
            visible: puedeEliminar,
            onClick: () => pedirEliminar(row),
          },
        ]}
      />

      {showModal ? (
        <AddBono
          id={id}
          setShowModal={setShowModal}
          reload={localReload}
          setReload={setLocalReload}
        />
      ) : null}

      {showEditModal ? (
        <UpdateBono
          selected={selectedBono}
          setShowModal={setShowEditModal}
          reload={localReload}
          setReload={setLocalReload}
        />
      ) : null}

      <MediumModal
        titulo={`Comprobante de Pago Bono`}
        isVisible={showPrintModal}
        setVisible={setShowPrintModal}
      >
        <PrintComprobante selected={selectedBono} setShowModal={setShowPrintModal} />
      </MediumModal>

      {dialogo}
    </div>
  );
};

export default Bonos;
